'use client';

import { useIntersectionTracking } from 'providers/OptimizelyProvider';
import { OPTIMIZELY_EVENT_VALUES } from 'providers/OptimizelyProvider/utils';
import { Suspense, useEffect } from 'react';
import { Paywall } from '@/app_components/Paywall/Paywall';
import { useAdContext } from '@/app_lib/providers/AdProvider';
import { useUser } from '@/app_lib/providers/UserProvider';
import { GoogleTagManager } from '@/app_lib/services/Gtm';
import type { PageAdConfig } from '@/app_lib/types/Ad';
import type { ArcSpanPageConfig } from '@/app_lib/types/ArcSpan';
import { PianoConfig, type PianoPageConfig } from '@/app_lib/types/accounts/piano/Piano';
import type { PageViewDataLayerObject } from '@/app_lib/types/Gtm';
import { cn } from '@/app_lib/utils/utils';

/**
 * ArticleWrapper component sets up client-sided page-level configurations.
 *
 * @param children - React children nodes to be rendered inside the wrapper.
 * @param pageConfig - Configuration object for Piano page settings.
 * @param arcSpanConfig - Configuration object for ArcSpan page settings.
 * @param pageAdConfig - Configuration object for page advertisement settings.
 * @param url - The canonical URL of the article, used for tracking.
 *
 * @remarks
 * - Initializes Piano, ArcSpan, and Ad context configurations on mount.
 * - Tracks optimizely events.
 * - Renders a paywall component after the children.
 * - For some reason, if we pass in the article itself we get some oddities with the byline which uses radix and the article.
 *   I have no clue why this is happening so avoid passing in the article itself for now.
 */
export const ArticleWrapper = ({
  children,
  pageConfig,
  arcSpanConfig,
  pageAdConfig,
  url,
  dataLayer,
  paywallBypass,
  noPaywall = false,
  className = '',
}: {
  children?: React.ReactNode;
  pageConfig: PianoPageConfig;
  arcSpanConfig: ArcSpanPageConfig;
  pageAdConfig: PageAdConfig;
  url: string;
  dataLayer: PageViewDataLayerObject;
  paywallBypass?: {
    bypass: boolean;
    reason: string;
  };
  noPaywall?: boolean;
  className?: string;
}) => {
  const { setPianoPageConfig } = useUser();
  const { setPageAdConfig, setArcSpanPageConfig } = useAdContext();

  useEffect(() => {
    const searchParams = new URL(window.location.href).searchParams; // Definitely don't want a suspense on the whole article, so using window here
    const bypassPaywall = paywallBypass?.bypass ? searchParams.has(paywallBypass.reason) : false;

    // If we want to editorial bypass the paywall or we have a no paywall article, set the content section to bypass
    pageConfig.contentSection = bypassPaywall || noPaywall ? PianoConfig.BypassSection : pageConfig.contentSection;
    setPianoPageConfig(pageConfig);
    setArcSpanPageConfig(arcSpanConfig);
    setPageAdConfig(pageAdConfig);
  }, [
    setPianoPageConfig,
    pageConfig,
    arcSpanConfig,
    pageAdConfig,
    setPageAdConfig,
    setArcSpanPageConfig,
    paywallBypass,
    noPaywall,
  ]);

  // biome-ignore-start lint/style/useNamingConvention: <external data>

  useIntersectionTracking(
    'author-bio',
    { URL: url, value: OPTIMIZELY_EVENT_VALUES.finishedArticle },
    'finished_article',
  );
  useIntersectionTracking('related-video', { URL: url, value: OPTIMIZELY_EVENT_VALUES.stnPlayer }, 'saw_stn_video');
  useIntersectionTracking(
    'outbrain-container',
    { URL: url, value: OPTIMIZELY_EVENT_VALUES.defaultAdImpression },
    'saw_outbrain',
  );

  // useIntersectionTracking('latest-popular-module', { URL: url, value: 1 }, 'saw_related_articles'); // TODO: re-enable when module is added
  // biome-ignore-end lint/style/useNamingConvention: <>

  return (
    <div className={cn('article-page-wrapper flex flex-col', className)}>
      <Suspense fallback={null}>
        <GoogleTagManager dataLayer={dataLayer} />
      </Suspense>
      {children}
      <Paywall />
    </div>
  );
};
